import React from 'react';
import type { AlbumExport, ExportState } from '../../hooks/useAlbumExport';
import { PRIMARY, SECONDARY, plural } from './dialogParts';

type Choosing = Extract<ExportState, { status: 'choosing' }>;

export function LockedView({ exp, onRequestAccess }: { exp: AlbumExport; onRequestAccess?: () => void }) {
  const request = () => {
    exp.close();
    onRequestAccess?.();
  };
  return (
    <div className="space-y-3">
      <p className="text-gray-700">You have view-only access to this album. Saving its photos needs Contributor access or higher.</p>
      {onRequestAccess
        ? <button type="button" className={PRIMARY} onClick={request}>Request access</button>
        : <p className="text-sm text-gray-500">Sign in to Kizu to ask the album's admins for access.</p>}
      <button type="button" className={SECONDARY} onClick={exp.close}>Close</button>
    </div>
  );
}

function HistoryNote({ exp, state }: { exp: AlbumExport; state: Choosing }) {
  const previous = state.items.length - state.fresh.length;
  if (previous === 0) return null;
  const when = state.lastExportedAt ? ` on ${new Date(state.lastExportedAt).toLocaleDateString()}` : '';
  return (
    <div className="space-y-2 rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
      <p>{previous} already exported from this browser{when}.</p>
      {state.fresh.length > 0 ? (
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={state.scope === 'new'} onChange={event => exp.setScope(event.target.checked ? 'new' : 'all')} />
          <span>Only export the {plural(state.fresh.length, 'new item')}</span>
        </label>
      ) : (
        <p>Nothing new since then. Exporting again includes everything.</p>
      )}
    </div>
  );
}

export function ChooseView({ exp, state }: { exp: AlbumExport; state: Choosing }) {
  const chosen = state.scope === 'new' ? state.fresh : state.items;
  const photos = chosen.filter(i => i.mediaType === 'photo').length;
  const videos = chosen.filter(i => i.mediaType === 'video').length;
  const others = chosen.length - photos - videos;
  const counts = [plural(photos, 'photo'), videos > 0 && plural(videos, 'video'), others > 0 && plural(others, 'other file')].filter(Boolean).join(', ');
  const hasItems = state.items.length > 0;
  return (
    <div className="space-y-3">
      {hasItems
        ? <p className="text-gray-700">{counts}, saved as the original files with a page for browsing them offline.</p>
        : <p className="text-gray-700">There is nothing to export from this album.</p>}
      <HistoryNote exp={exp} state={state} />
      {state.unavailable > 0 && (
        <p className="text-sm text-amber-700">{plural(state.unavailable, 'item')} can't be exported because the original isn't stored in Kizu.</p>
      )}
      {state.notice && <p className="text-sm text-red-600">{state.notice}</p>}
      {hasItems && exp.supportsFolder && (
        <button type="button" className={PRIMARY} onClick={exp.exportToFolder}>Save to a folder</button>
      )}
      {hasItems && (
        <button type="button" className={exp.supportsFolder ? SECONDARY : PRIMARY} onClick={exp.exportToZip}>Download as ZIP</button>
      )}
      <button type="button" className={SECONDARY} onClick={exp.close}>Close</button>
    </div>
  );
}
